import { PhiNganHangRow } from '../components/form-dialog/hooks/usePhiNganHangRows';
import { DetailRow } from '../components/form-dialog/hooks/useDetailRows';
import { ThueRow } from '../components/form-dialog/hooks/useThueRows';

interface TaxLookupValue {
  thue_suat?: number | string | null;
  tax_rate?: number | string | null;
  ma_thue_data?: TaxLookupValue | null;
}

/**
 * Calculate totals from detail rows
 */
export const calculateTotals = (
  detailRows: DetailRow[] = [],
  taxRows: ThueRow[] = [],
  feeRows: PhiNganHangRow[] = []
) => {
  const tong_tien = Math.round(detailRows.reduce((sum, row) => sum + (Number(row.tien_nt) || 0), 0) * 10) / 10;
  const tong_thue = Math.round(taxRows.reduce((sum, row) => sum + (Number(row.t_thue_nt) || 0), 0) * 10) / 10;
  const tong_phi =
    Math.round(
      feeRows.reduce((sum, row) => sum + (Number(row.tien_cp_nt) || 0) + (Number(row.t_thue_nt) || 0), 0) * 10
    ) / 10;

  const tong_thanh_toan = tong_tien + tong_thue + tong_phi;

  return {
    tong_tien,
    tong_thue,
    tong_phi,
    tong_thanh_toan
  };
};

/**
 * Handle field value changes for detail rows with complex tax calculations
 */
export const handleDetailFieldChange = (row: DetailRow, field: string, newValue: unknown): Partial<DetailRow> => {
  const updates: Partial<DetailRow> = {};
  const lookupValue = newValue as TaxLookupValue | null;

  if (field === 'id_hd_data') {
    updates.tien_nt = (newValue as DetailRow['id_hd_data'])?.tien_tren_hd;
    updates.tk_no_data = (newValue as DetailRow['id_hd_data'])?.tk_data;
  }

  if (field === 'ma_thue_data' && lookupValue) {
    const thue_suat = Number(lookupValue.thue_suat || lookupValue.tax_rate) || 0;
    const t_tien_nt = Number(row.t_tien_nt) || 0;
    const tien_cp_nt = Number(row.tien_cp_nt) || 0;

    const thue_t_tien = t_tien_nt > 0 ? (t_tien_nt * thue_suat) / 100 : 0;

    const thue_tien_cp = tien_cp_nt > 0 ? (tien_cp_nt * thue_suat) / 100 : 0;

    const t_thue_nt = thue_t_tien + thue_tien_cp;

    updates.t_thue_nt = t_thue_nt;
  }

  if (field === 't_tien_nt') {
    const ma_thue_data = row.ma_thue_data;
    if (ma_thue_data) {
      const thue_suat = Number(ma_thue_data.thue_suat || ma_thue_data.tax_rate) || 0;
      const t_tien_nt = Number(newValue) || 0;
      const tien_cp_nt = Number(row.tien_cp_nt) || 0;

      const thue_t_tien = (t_tien_nt * thue_suat) / 100;

      const thue_tien_cp = tien_cp_nt > 0 ? (tien_cp_nt * thue_suat) / 100 : 0;

      const t_thue_nt = thue_t_tien + thue_tien_cp;

      updates.t_thue_nt = t_thue_nt;
    }
  }

  if (field === 'tien_cp_nt') {
    const ma_thue_data = row.ma_thue_data;
    const tien_cp_nt = Number(newValue) || 0;

    updates.tien_cp_nt = tien_cp_nt;

    if (ma_thue_data) {
      const thue_suat = Number(ma_thue_data.thue_suat || ma_thue_data.tax_rate) || 0;
      const t_tien_nt = Number(row.t_tien_nt) || 0;

      const thue_t_tien = t_tien_nt > 0 ? (t_tien_nt * thue_suat) / 100 : 0;

      const thue_tien_cp = (tien_cp_nt * thue_suat) / 100;

      const t_thue_nt_total = thue_t_tien + thue_tien_cp;

      updates.t_thue_nt = t_thue_nt_total;
    } else {
      const t_tien_nt = Number(row.t_tien_nt) || 0;
      if (t_tien_nt === 0) {
        updates.t_thue_nt = 0;
      }
    }
  }

  return updates;
};

/**
 * Handle field value changes for tax rows
 */
export const handleThueFieldChange = (_row: ThueRow, _field: string, _newValue: unknown): Partial<ThueRow> => {
  const updates: Partial<ThueRow> = {};

  return updates;
};

/**
 * Handle field value changes for bank fee rows
 */
export const handleFieldChange = (row: PhiNganHangRow, field: string, newValue: unknown): Partial<PhiNganHangRow> => {
  const patch: Partial<PhiNganHangRow> = {};
  const lookupValue = newValue as TaxLookupValue | null;

  if (field === 'ma_cpnh_data' && lookupValue?.ma_thue_data) {
    patch.ma_thue_data = lookupValue.ma_thue_data as PhiNganHangRow['ma_thue_data'];
  }

  if (['tien_cp_nt', 'ma_thue_data', 'ma_cpnh_data'].includes(field)) {
    const tien_cp_nt = field === 'tien_cp_nt' ? Number(newValue) || 0 : Number(row.tien_cp_nt) || 0;

    let thue_suat = 0;
    if (field === 'ma_thue_data') {
      thue_suat = Number(lookupValue?.thue_suat) || 0;
    } else if (field === 'ma_cpnh_data' && lookupValue?.ma_thue_data) {
      thue_suat = Number(lookupValue.ma_thue_data.thue_suat) || 0;
    } else {
      thue_suat = Number(row.ma_thue_data?.thue_suat) || 0;
    }

    patch.t_thue_nt = Math.round((tien_cp_nt * thue_suat) / 100);
  }

  return patch;
};
